/**
 * Fail-closed network egress guard for guest mode.
 *
 * Covers: window.fetch, XMLHttpRequest, default axios, and any axios instance
 * that registers via `registerGuestAxiosGuard`. blob:/data: URLs are allowed
 * (tour PDF generation). Everything else is blocked.
 *
 * In strict mode (VITE_APP_GUEST_STRICT=true) blocked calls throw. Otherwise
 * they resolve with an inert empty payload so the demo degrades quietly.
 */
import axios, { type AxiosInstance, type InternalAxiosRequestConfig } from 'axios'
import { GUEST_MODE, isGuestStrictMode } from '@/guest/guestMode'

let installed = false

function isAllowedUrl(url: string): boolean {
    if (!url) return true
    const lower = url.toLowerCase()
    if (lower.startsWith('blob:') || lower.startsWith('data:')) return true
    // Same-origin relative asset loads (Vite modules, CSS, fonts from /)
    // are not an issue — but fetch/XHR rarely hit those. Be conservative:
    // only blob/data are allowlisted for fetch/XHR.
    return false
}

function blockReason(kind: string, url: string): string {
    return `[guest] blocked ${kind}: ${url}`
}

function handleBlocked(kind: string, url: string): never | void {
    const msg = blockReason(kind, url)
    if (isGuestStrictMode()) {
        throw new Error(msg)
    }
    console.warn(msg)
}

function inertAxiosResponse(config: InternalAxiosRequestConfig) {
    return Promise.resolve({
        data: {},
        status: 200,
        statusText: 'OK (guest blocked)',
        headers: {},
        config,
    })
}

/** Register a guest-mode request interceptor on any axios instance. */
export function registerGuestAxiosGuard(client: AxiosInstance): void {
    if (!GUEST_MODE) return
    client.interceptors.request.use((config) => {
        const url = `${config.baseURL || ''}${config.url || ''}`
        if (isAllowedUrl(url)) return config
        handleBlocked('axios', url)
        // Reject the request chain and fulfill via adapter stub.
        const err: any = new Error(blockReason('axios', url))
        err.__guestBlocked = true
        err.config = config
        return Promise.reject(err)
    })
    client.interceptors.response.use(
        (r) => r,
        (error) => {
            if (error?.__guestBlocked && error.config) {
                return inertAxiosResponse(error.config)
            }
            return Promise.reject(error)
        },
    )
}

function installFetchGuard(): void {
    if (typeof window === 'undefined' || !window.fetch) return
    const original = window.fetch.bind(window)
    window.fetch = async (input: RequestInfo | URL, init?: RequestInit) => {
        const url =
            typeof input === 'string'
                ? input
                : input instanceof URL
                  ? input.href
                  : input.url
        if (isAllowedUrl(url)) {
            return original(input, init)
        }
        handleBlocked('fetch', url)
        return new Response(JSON.stringify({}), {
            status: 200,
            statusText: 'OK (guest blocked)',
            headers: { 'Content-Type': 'application/json' },
        })
    }
}

function installXhrGuard(): void {
    if (typeof XMLHttpRequest === 'undefined') return
    const originalOpen = XMLHttpRequest.prototype.open
    XMLHttpRequest.prototype.open = function (
        method: string,
        url: string | URL,
        ...rest: any[]
    ) {
        const urlStr = typeof url === 'string' ? url : url.href
        if (!isAllowedUrl(urlStr)) {
            handleBlocked('xhr', urlStr)
            // Point at a no-op data URL so send() does nothing useful.
            return originalOpen.call(
                this,
                method,
                'data:application/json,{}',
                ...rest,
            )
        }
        return originalOpen.call(this, method, url, ...rest)
    }
}

/**
 * Install all egress guards. Safe to call once; subsequent calls are no-ops.
 * Must run before any API module fires a request.
 */
export function installEgressGuard(): void {
    if (!GUEST_MODE || installed) return
    installed = true

    installFetchGuard()
    installXhrGuard()
    registerGuestAxiosGuard(axios)
}
